<template>
    <div
        class="aon-input"
        :class="{
            'is-search-input': searchInput ,
            'has-icon': iconOptions ,
            disabled: disabled,
            'error-alert': error,
        }"
    >
        <div class="label-holder">
            <label v-if="label" class="mb-1">{{ label }}</label>
            <span
                class="link"
                v-if="informationIcon"
                v-tooltip="{
                    content: informationIcon,
                    delay: { show: 500, hide: 100 },
                }"
            >
                <font-awesome-icon icon="fal fa-circle-info" class="icon grey02--text" />
            </span>
        </div>
        <p v-if="description" class="mb-1">{{ description }}</p>
        <AonAlertBox
            v-if="error && errorOptions.errorMsg !== ''"
            class="mb-2"
            :isContextual="true"
            :title="errorOptions.errorMsg"
            type="error"
        />
        <div class="input-wrapper">
            <span
                v-if="searchInput"
                class="icon-container search-icon"
                @click="$emit('search')"
            >
                <font-awesome-icon icon="fal fa-magnifying-glass" class="icon grey02--text" />
            </span>
            <input
                ref="inputElement"
                class="overflow-ellipsis"
                :id="id"
                :type="type"
                :placeholder="placeholder"
                v-model="valueFinal"
                @keyup="input"
            />
            <span
                v-if="iconOptions && !searchInput"
                class="icon-container"
                v-tooltip="{
                    content: iconOptions.tooltipText,
                    delay: { show: 500, hide: 100 }
                }"
                @click="$emit('icon-clicked')"
            >
                <font-awesome-icon :icon="`${iconOptions.iconStyle} ${iconOptions.iconName}`" class="icon castle-moat--text" />
            </span>
            <span
                v-if="searchInput && hasSearchTerm"
                class="icon-container"
                @click="clear"
            >
                <font-awesome-icon icon="far fa-xmark" class="icon castle-moat--text" />
            </span>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, watch, defineExpose, onMounted } from 'vue';
import AonAlertBox from '../AonAlertBox.vue';

const props = defineProps({
    modelValue: {
        type: [Number, String],
        default: null,
    },
    type: {
        type: String,
        default: 'text'
    },
    label: {
        type: String,
        default: null,
    },
    description: {
        type: String,
        default: ''
    },
    id: {
        type: String,
        default: ''
    },
    focus: {
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    searchInput: {
        type: Boolean,
        default: false,
    },
    iconOptions: {
        type: Object,
        default: () => {},
    },
    informationIcon: {
        type: String,
        default: ''
    },
    // Hoping to have some sort of validator for errors
    error: {
        type: Boolean,
        default: false,
    },
    errorOptions: {
        type: Object,
        default: () => {
            return {
                errorMsg: ''
            }
        },
    },
    placeholder: {
        type: String,
        default: ''
    }
})
const emit = defineEmits(['clear', 'clicked', 'update:modelValue'])

const inputElement = ref(null)

const hasSearchTerm = computed(() => {
    if (props.searchInput && valueFinal.value) {
        return valueFinal.value.length >= 2;
    } else {
        return false;
    }
})

const valueFinal = computed({
    get() {
        return props.modelValue
    },
    set(value) {
        emit('update:modelValue', value)
    }
})

const clear = () => {
    valueFinal.value = '';
    emit('clear');
    setFocus();
}

const input = () => {
    emit('update:modelValue', valueFinal.value);
}

const setFocus = () => {
    inputElement.value.focus();
}

watch(() => props.value, (newVal, oldVal) => {
    valueFinal.value = newVal;
})

onMounted(() => {
    if (props.focus) {
        setFocus();
    }
})

defineExpose({ setFocus })
</script>

<script>
    export default {
        name: 'AonInput'
    }
</script>

<style lang="scss">
.aon-input {
    width: 100%;

    &.width-auto {
        width: auto;
    }

    &.is-search-input {
        input {
            padding-left: 34px !important;
        }
    }

    &.has-icon {
        input {
            padding-right: 34px !important;
        }
    }

    .input-wrapper {
        position: relative;
    }

    .icon-container {
        width: 17px;
        height: 17px;
        position: absolute;
        right: 11px;
        top: 13px;
        cursor: pointer;

        &.search-icon {
            right: auto;
            left: 11px;
            top: 12px;
        }

        .icon {
            width: 100%;
            height: 100%;
        }
    }

    .label-holder {
        display: flex;
        justify-content: space-between;
    }
}
</style>
